import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import style from "./SubscriptionDetailsStyle";
import {
  getSubscriptionDetails,
  updateSubscription,
  renewSubscription, // Importar nova função
  getAllSubscriptionTypes, // Importar nova função
} from "../../../Services/subscriptionService";
import toast from "react-hot-toast";

export default function SubscriptionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]); // Novo estado para os tipos de plano
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false); // Novo estado para o loading da renovação

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Usando Promise.all para carregar dados em paralelo
      const [data, types] = await Promise.all([
        getSubscriptionDetails(id),
        getAllSubscriptionTypes(),
      ]);

      if (data) {
        setDetails(data);
        setFormData(data.subscription);
        setSubscriptionTypes(types);
      } else {
        toast.error("Assinatura não encontrada.");
        navigate("/subscriptions");
      }
    } catch (error) {
      toast.error("Falha ao carregar detalhes da assinatura.");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value, 10) : value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedData = await updateSubscription(id, formData);
      // Recarrega os dados para garantir que tudo esteja atualizado, inclusive o nome do plano
      await fetchData(); 
      setIsEditing(false);
      toast.success("Assinatura atualizada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Falha ao salvar.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRenew = async () => {
    setIsRenewing(true);
    try {
      const renewedData = await renewSubscription(id);
      setDetails(prev => ({...prev, subscription: renewedData}));
      setFormData(renewedData);
      toast.success("Assinatura renovada com sucesso!");
    } catch (error) {
       toast.error(error.response?.data?.message || "Falha ao renovar.");
    } finally {
      setIsRenewing(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  if (loading || !details) {
    return <div style={style.loadingContainer}>Carregando Detalhes...</div>;
  }

  const { enterprise, subscription, subscriptionType } = details;

  return (
    <div style={style.container}>
      <header style={style.header}>
        <div>
          <h1 style={style.title}>Detalhes da Assinatura</h1>
          <p style={style.subtitle}>
            Plano {subscriptionType.name} de {enterprise.name}
          </p>
        </div>
        <button
          style={style.backButton}
          onClick={() => navigate("/subscriptions")}
        >
          &larr; Voltar
        </button>
      </header>

      <div style={style.contentGrid}>
        <div
          style={style.enterpriseCard}
          onClick={() => navigate(`/platforms/${enterprise.id}`)}
        >
          <h3 style={style.cardTitle}>Cliente Associado</h3>
          <p style={style.enterpriseName}>{enterprise.name}</p>
          <p style={style.enterpriseInfo}>ID: {enterprise.id}</p>
          <p style={style.enterpriseInfo}>CNPJ: {enterprise.cnpj}</p>
          <span style={style.cardLink}>Ver Cliente &rarr;</span>
        </div>

        <div style={style.detailsCard}>
          <div style={style.cardHeader}>
            <h3 style={style.cardTitle}>Informações da Assinatura</h3>
            {!isEditing && (
                <button 
                  onClick={handleRenew}
                  style={{...style.actionButton, ...style.renewButton}}
                  disabled={isRenewing}
                >
                  {isRenewing ? 'Renovando...' : 'Renovar Assinatura'}
                </button>
            )}
          </div>
          <div style={style.formGrid}>
            {/* Tipo de Plano */}
            <div style={style.formGroup}>
              <label style={style.label}>Tipo de Plano</label>
              {isEditing ? (
                <select
                  name="subscriptionTypeId"
                  value={formData.subscriptionTypeId}
                  onChange={handleChange}
                  style={style.input}
                >
                  {subscriptionTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              ) : (
                <p style={style.value}>{subscriptionType.name}</p>
              )}
            </div>
            {/* Status */}
            <div style={style.formGroup}>
              <label style={style.label}>Status</label>
              {isEditing ? (
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={style.input}
                >
                  <option value={1}>Pendente</option>
                  <option value={2}>Ativa</option>
                  <option value={3}>Bloqueada</option>
                </select>
              ) : (
                <p style={style.value}>
                  {{ 1: "Pendente", 2: "Ativa", 3: "Bloqueada" }[subscription.status]}
                </p>
              )}
            </div>
            {/* Data de Expiração */}
            <div style={style.formGroup}>
              <label style={style.label}>Data de Expiração</label>
              {isEditing ? (
                <input
                  type="date"
                  name="expirationDate"
                  value={formatDateForInput(formData.expirationDate)}
                  onChange={handleChange}
                  style={style.input}
                />
              ) : (
                <p style={style.value}>
                  {new Date(subscription.expirationDate).toLocaleDateString("pt-BR")}
                </p>
              )}
            </div>
            {/* Disparos Disponíveis */}
            <div style={style.formGroup}>
              <label style={style.label}>Disparos Disponíveis</label>
              {isEditing ? (
                <input
                  type="number"
                  name="avaliableShots"
                  value={formData.avaliableShots}
                  onChange={handleChange}
                  style={style.input}
                />
              ) : (
                <p style={style.value}>{subscription.avaliableShots}</p>
              )}
            </div>
            {/* Usuários Disponíveis */}
            <div style={style.formGroup}>
              <label style={style.label}>Usuários Disponíveis</label>
              {isEditing ? (
                <input
                  type="number"
                  name="avaliableUsers"
                  value={formData.avaliableUsers}
                  onChange={handleChange}
                  style={style.input}
                />
              ) : (
                <p style={style.value}>{subscription.avaliableUsers}</p>
              )}
            </div>
          </div>
          <footer style={style.cardFooter}>
            {isEditing ? (
              <>
                <button
                  onClick={() => { setIsEditing(false); setFormData(subscription); }}
                  style={{ ...style.actionButton, ...style.cancelButton }}
                  disabled={isSaving}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  style={{ ...style.actionButton, ...style.saveButton }}
                  disabled={isSaving}
                >
                  {isSaving ? "Salvando..." : "Salvar Alterações"}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                style={{ ...style.actionButton, ...style.editButton }}
              >
                Editar Assinatura
              </button>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
}